/**
 * Pending-mutations bookkeeping (Phase 5).
 *
 * The split-canonical write protocol can fan out a single client edit
 * across two scopes (shared half → SG, personal half → UserDo overlay).
 * Each half is acked separately. This module tracks "what's in flight"
 * so the client knows when a mutation is fully committed (every
 * expected scope acked) versus partially landed (one scope acked, the
 * other still pending or rejected).
 *
 * State machine per mutationId:
 *   `pending`  — registered; waiting for at least one ack/reject.
 *   `acked`    — every expected scope acknowledged. Pre-image discarded.
 *   `rejected` — at least one scope rejected. Caller is expected to
 *                revert the pre-image and surface the toast.
 *   `partial`  — at least one scope acked AND at least one rejected.
 *                Caller still reverts; the acked half stays applied.
 *   `timeout`  — defensive: client was online for >5s after dispatch
 *                without seeing all expected acks. Caller reverts.
 *
 * No React, no Zustand, no DOM. Side-effect-free except for the
 * `setTimeout` it manages internally — exposed for unit tests via the
 * `clock` injection point on `createPendingMutationsTracker`.
 */

export type Scope = 'shared' | 'personal';

/** Reason codes mirroring the wire `OpRejectedMessage.reason`. */
export type RejectReason =
  | 'unauthorized'
  | 'wrong-scope-shared-on-linked'
  | 'wrong-scope-personal-on-shared'
  | 'stale-lww'
  | 'rpc-failed'
  | 'timeout'
  | 'validation'
  | 'unknown';

export type PendingState = {
  mutationId: string;
  expectedScopes: Set<Scope>;
  ackedScopes: Set<Scope>;
  rejectedScopes: Map<Scope, RejectReason>;
  /** Set by `markSent` when the outbound frame hits the wire. Until
   *  that point the mutation is "registered but not yet sent" and no
   *  timeout clock runs — this is what stops batching latency or
   *  offline-outbox time from tripping false rejections. */
  sentAt?: number;
  timerHandle?: ReturnType<typeof setTimeout>;
};

export type SettlementOutcome =
  | { kind: 'pending' }
  | { kind: 'acked' }
  | { kind: 'partial'; rejected: Map<Scope, RejectReason> }
  | { kind: 'rejected'; rejected: Map<Scope, RejectReason> }
  | { kind: 'timeout'; expected: Set<Scope>; acked: Set<Scope> };

export type Tracker = {
  /** Register a new mutation with the set of scopes whose acks are expected.
   *  Does NOT start the timeout — call `markSent` once the message actually
   *  reaches the wire. Pre-`markSent` mutations live forever (or until
   *  ack/reject/clear) so batching/outbox latency doesn't trip false
   *  timeouts. */
  register(mutationId: string, expectedScopes: Scope[]): void;
  /** Record that the outbound frame for this mutation just hit the wire.
   *  Re-bases the timeout clock to `now()` and starts the timer if the
   *  tracker is armed. Idempotent — calling it twice is a no-op. */
  markSent(mutationId: string): void;
  /** Mark a scope as acknowledged. Returns the post-update outcome. */
  ack(mutationId: string, scope?: Scope): SettlementOutcome;
  /** Mark a scope as rejected. Returns the post-update outcome. */
  reject(mutationId: string, reason: RejectReason, scope?: Scope): SettlementOutcome;
  /** Manually time out a mutation (used by reconnect logic). */
  timeoutMutation(mutationId: string): SettlementOutcome;
  /** Get the current outcome for a mutation, or `null` if unknown. */
  inspect(mutationId: string): SettlementOutcome | null;
  /**
   * Subscribe to the single settled-state transition for one mutation.
   * Fires AT MOST ONCE when the tracker enters a non-`pending` state
   * (`acked` / `rejected` / `partial` / `timeout`) and is then
   * automatically unsubscribed. Returns an `unsubscribe` thunk for
   * callers that want to give up early (e.g. component unmount).
   *
   * The callback fires synchronously from inside `ack`/`reject`/
   * `timeoutMutation`/the internal timer, AFTER the pending entry is
   * deleted — so a caller awaiting this promise can safely call
   * `inspect()` to confirm the entry is gone. If the mutation is
   * already settled or never registered at the time of subscription,
   * the callback fires on the next microtask with `null` outcome so
   * await-ers don't hang.
   *
   * Multiple subscribers per mutationId are allowed; all fire in
   * registration order.
   */
  onSettled(
    mutationId: string,
    cb: (outcome: SettlementOutcome | null) => void,
  ): () => void;
  /** Drop all pending mutations. Used on signout / disconnect. */
  clear(): void;
  /** Test-only: snapshot current pending state. */
  _snapshot(): Map<string, PendingState>;
};

const DEFAULT_TIMEOUT_MS = 5_000;

type CreateOpts = {
  /** Override Date.now (for tests). */
  now?: () => number;
  /** Override timeout window. */
  timeoutMs?: number;
  /**
   * Called when a registered mutation hits its timeout. Caller is
   * expected to revert the pre-image and emit a toast. Receives the
   * outcome in `kind: "timeout"` form.
   *
   * NOTE: timeouts only fire while connected (see `arm()` / `disarm()`).
   * Offline writes wait for reconnect.
   */
  onTimeout?: (mutationId: string, outcome: SettlementOutcome) => void;
};

/**
 * Construct a stateful tracker. One per session.
 *
 * Timeouts only fire when the tracker is `armed` (connected). Call
 * `disarm()` on disconnect to pause the timers; `arm()` on reconnect
 * to re-start countdowns from the original `sentAt`. New mutations
 * registered while disarmed do not start a timer until the next
 * `arm()` call.
 */
export function createPendingMutationsTracker(opts: CreateOpts = {}): Tracker & {
  arm(): void;
  disarm(): void;
  isArmed(): boolean;
} {
  const now = opts.now ?? Date.now;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const onTimeout = opts.onTimeout;

  const pending = new Map<string, PendingState>();
  /**
   * `onSettled` subscribers keyed by mutationId. Populated by
   * `onSettled(...)`, drained + cleared whenever a mutation reaches
   * a settled state (any outcome except `pending`).
   *
   * Kept separate from `pending` so a subscription registered AFTER
   * the mutation already settled (race between the WS ack and a
   * React effect that subscribes) still gets notified — see
   * `onSettled`'s microtask fallback.
   */
  const subscribers = new Map<string, Set<(outcome: SettlementOutcome | null) => void>>();
  let armed = false;

  function clearTimer(state: PendingState) {
    if (state.timerHandle) {
      clearTimeout(state.timerHandle);
      state.timerHandle = undefined;
    }
  }

  /**
   * Fire every `onSettled` subscriber for `mutationId` exactly once
   * and remove them. Safe to call from inside the settlement paths
   * (`ack`/`reject`/`timeoutMutation`/timer fire) AFTER the
   * `pending` entry was deleted — callers' `inspect()` reads will
   * correctly see "unknown".
   *
   * Subscribers fire in registration order. Errors from one
   * subscriber don't block the others (try/catch per call) — this
   * matters because `waitForMutationOutcome` resolves a promise from
   * inside the subscriber, and we don't want one consumer's broken
   * `.then` to swallow another's resolve.
   */
  function fireSettled(mutationId: string, outcome: SettlementOutcome): void {
    const subs = subscribers.get(mutationId);
    if (!subs || subs.size === 0) return;
    subscribers.delete(mutationId);
    for (const cb of subs) {
      try {
        cb(outcome);
      } catch (err) {
        // Silent — same defensive policy as upstream Observable
        // helpers. Surface via dev-tools / Sentry-side breadcrumb
        // if a subscriber starts throwing systematically.
        console.error('[pending-mutations] onSettled subscriber threw', err);
      }
    }
  }

  function startTimer(state: PendingState) {
    if (!armed) return;
    if (state.timerHandle) return;
    // No timer until the mutation has actually been sent. Pre-`markSent`
    // mutations sit in `pending` indefinitely; the next `arm()` /
    // `markSent` will start the clock.
    if (state.sentAt === undefined) return;
    const elapsed = now() - state.sentAt;
    const remaining = Math.max(0, timeoutMs - elapsed);
    state.timerHandle = setTimeout(() => {
      // Timer fire == timeout. The mutation may have partial acks at
      // this point, but we treat any not-fully-acked-by-deadline state
      // as a timeout so the caller can revert.
      state.timerHandle = undefined;
      pending.delete(state.mutationId);
      const outcome: SettlementOutcome = {
        kind: 'timeout',
        expected: state.expectedScopes,
        acked: state.ackedScopes,
      };
      onTimeout?.(state.mutationId, outcome);
      fireSettled(state.mutationId, outcome);
    }, remaining);
  }

  function settle(state: PendingState): SettlementOutcome {
    const totalExpected = state.expectedScopes.size;
    const totalAcked = state.ackedScopes.size;
    const totalRejected = state.rejectedScopes.size;
    if (totalExpected === 0 || totalAcked + totalRejected < totalExpected) {
      return { kind: 'pending' };
    }
    if (totalRejected === 0) return { kind: 'acked' };
    if (totalAcked === 0) return { kind: 'rejected', rejected: state.rejectedScopes };
    return { kind: 'partial', rejected: state.rejectedScopes };
  }

  function inferDefaultScope(state: PendingState): Scope {
    // Default scope when a server forgets to label the ack/reject:
    // pick the only expected scope (most common — single-scope edit).
    // For mixed-scope mutations (size 2), default to "personal" to
    // keep the older non-scoped wire format addressing the personal
    // half (UserDo). Better: the server should always send `scope`.
    if (state.expectedScopes.size === 1) {
      return state.expectedScopes.values().next().value as Scope;
    }
    return 'personal';
  }

  return {
    arm(): void {
      armed = true;
      // Re-arm timers for any pending mutations. Mutations whose sent-at
      // is already past the timeout window (e.g. a long offline period
      // ended) are settled synchronously here — otherwise startTimer
      // would schedule a 0ms setTimeout and fire them all on the next
      // tick anyway, which made the burst-timeout-on-reconnect behavior
      // implicit. Calling onTimeout in arm() makes that intent explicit
      // and removes a tick of latency between reconnect and revert.
      const expired: PendingState[] = [];
      for (const state of pending.values()) {
        if (state.sentAt !== undefined && now() - state.sentAt >= timeoutMs) {
          expired.push(state);
        } else {
          startTimer(state);
        }
      }
      for (const state of expired) {
        clearTimer(state);
        pending.delete(state.mutationId);
        const outcome: SettlementOutcome = {
          kind: 'timeout',
          expected: state.expectedScopes,
          acked: state.ackedScopes,
        };
        onTimeout?.(state.mutationId, outcome);
        fireSettled(state.mutationId, outcome);
      }
    },
    disarm(): void {
      armed = false;
      for (const state of pending.values()) clearTimer(state);
    },
    isArmed(): boolean {
      return armed;
    },
    register(mutationId, expectedScopes): void {
      if (pending.has(mutationId)) {
        // Overwrite — stale entry from a previous attempt with the
        // same id (shouldn't happen with UUIDv4 but be defensive).
        const old = pending.get(mutationId)!;
        clearTimer(old);
      }
      const state: PendingState = {
        mutationId,
        expectedScopes: new Set(expectedScopes),
        ackedScopes: new Set(),
        rejectedScopes: new Map(),
        // sentAt deliberately left undefined — set by `markSent` when
        // the outbound frame reaches the wire. Until then no timer runs.
      };
      pending.set(mutationId, state);
    },
    markSent(mutationId): void {
      const state = pending.get(mutationId);
      if (!state) return;
      if (state.sentAt !== undefined) return; // idempotent
      state.sentAt = now();
      startTimer(state);
    },
    ack(mutationId, scope): SettlementOutcome {
      const state = pending.get(mutationId);
      if (!state) return { kind: 'pending' };
      const s = scope ?? inferDefaultScope(state);
      state.ackedScopes.add(s);
      const outcome = settle(state);
      if (outcome.kind !== 'pending') {
        clearTimer(state);
        pending.delete(mutationId);
        fireSettled(mutationId, outcome);
      }
      return outcome;
    },
    reject(mutationId, reason, scope): SettlementOutcome {
      const state = pending.get(mutationId);
      if (!state) return { kind: 'pending' };
      const s = scope ?? inferDefaultScope(state);
      state.rejectedScopes.set(s, reason);
      const outcome = settle(state);
      if (outcome.kind !== 'pending') {
        clearTimer(state);
        pending.delete(mutationId);
        fireSettled(mutationId, outcome);
      }
      return outcome;
    },
    timeoutMutation(mutationId): SettlementOutcome {
      const state = pending.get(mutationId);
      if (!state) return { kind: 'pending' };
      clearTimer(state);
      pending.delete(mutationId);
      const outcome: SettlementOutcome = {
        kind: 'timeout',
        expected: state.expectedScopes,
        acked: state.ackedScopes,
      };
      fireSettled(mutationId, outcome);
      return outcome;
    },
    inspect(mutationId): SettlementOutcome | null {
      const state = pending.get(mutationId);
      if (!state) return null;
      return settle(state);
    },
    onSettled(mutationId, cb): () => void {
      // Fast path: mutation already settled OR never registered.
      // Fire on a microtask so the caller can rely on consistent
      // async-then semantics — `waitForMutationOutcome` constructs
      // its Promise via `new Promise((resolve) => onSettled(...,
      // resolve))`, and the Promise must not synchronously resolve
      // mid-call (it would tangle the microtask ordering of any
      // surrounding `await`).
      if (!pending.has(mutationId)) {
        queueMicrotask(() => {
          // Re-check at fire time: a `register(mutationId, ...)`
          // could race the microtask. Standard subscribe-after-
          // register hand-off works correctly because the next
          // settlement will fire from the subscribers map.
          if (!pending.has(mutationId)) cb(null);
        });
        return () => {
          /* nothing scheduled to cancel beyond the microtask, which
             will short-circuit via the `pending.has` re-check above
             once we're unsubscribed — but the cb still fires once
             with `null`. Acceptable: callers treat `null` as "no
             settlement seen", same as a clear()-during-flight. */
        };
      }
      let subs = subscribers.get(mutationId);
      if (!subs) {
        subs = new Set();
        subscribers.set(mutationId, subs);
      }
      subs.add(cb);
      return () => {
        const s = subscribers.get(mutationId);
        if (!s) return;
        s.delete(cb);
        if (s.size === 0) subscribers.delete(mutationId);
      };
    },
    clear(): void {
      for (const state of pending.values()) clearTimer(state);
      pending.clear();
      // Drop pending subscribers without firing — `clear()` is the
      // signout / disconnect path; outstanding awaiters die with
      // their owning component context. Firing them would surface
      // bogus "timeout" toasts during a clean signout.
      subscribers.clear();
    },
    _snapshot(): Map<string, PendingState> {
      return new Map(pending);
    },
  };
}

/**
 * Generate a UUIDv4-shaped id for a new mutation. Falls back to a
 * timestamp-randomised string when crypto.randomUUID isn't available
 * (older browsers, some test environments).
 */
export function generateMutationId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
